import React from 'react';
import { Database, Search, AlertCircle, BarChart2 } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';

export function LogManagement() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Gestion des Logs',
        en: 'Log Management'
      },
      subtitle: {
        fr: 'Collecte centralisée et analyse avancée des logs',
        en: 'Centralized collection and advanced log analysis'
      }
    },
    features: [
      {
        icon: <Database className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Collecte Centralisée',
          en: 'Centralized Collection'
        },
        description: {
          fr: 'Agrégation de tous les logs dans une plateforme unique',
          en: 'Aggregation of all logs in a single platform'
        }
      },
      {
        icon: <Search className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Recherche Avancée',
          en: 'Advanced Search'
        },
        description: {
          fr: 'Recherche rapide et analyse détaillée des logs',
          en: 'Quick search and detailed log analysis'
        }
      },
      {
        icon: <BarChart2 className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Visualisation',
          en: 'Visualization'
        },
        description: {
          fr: 'Tableaux de bord personnalisables et rapports détaillés',
          en: 'Customizable dashboards and detailed reports'
        }
      }
    ],
    capabilities: {
      title: {
        fr: 'Fonctionnalités de Gestion',
        en: 'Management Features'
      },
      items: [
        {
          title: {
            fr: 'Rétention des Logs',
            en: 'Log Retention'
          },
          description: {
            fr: 'Conservation sécurisée des logs selon vos besoins',
            en: 'Secure log retention according to your needs'
          }
        },
        {
          title: {
            fr: 'Analyse en Temps Réel',
            en: 'Real-time Analysis'
          },
          description: {
            fr: 'Traitement et analyse instantanés des événements',
            en: 'Instant processing and analysis of events'
          }
        },
        {
          title: {
            fr: 'Conformité',
            en: 'Compliance'
          },
          description: {
            fr: 'Respect des normes de conformité et audit simplifié',
            en: 'Compliance standards adherence and simplified auditing'
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Database className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Capabilities Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.capabilities.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {content.capabilities.items.map((item, index) => (
              <div key={index} className="flex items-start space-x-4 bg-gray-900/50 p-6 rounded-lg">
                <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {item.title[language]}
                  </h3>
                  <p className="text-gray-300">
                    {item.description[language]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Centralisez Vos Logs' : 'Centralize Your Logs'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Optimisez votre gestion des logs avec notre solution centralisée' 
                : 'Optimize your log management with our centralized solution'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Commencer Maintenant' : 'Get Started Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}